import React, { useState, useEffect } from 'react';

const MutualConnections = ({ alumniId, mutualConnectionsCount }) => {
  const [mutualConnections, setMutualConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [error, setError] = useState('');
  const [hasLoaded, setHasLoaded] = useState(false); // Cache flag

  const fetchMutualConnections = async () => {
    if (!alumniId || mutualConnectionsCount === 0 || hasLoaded) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      
      const response = await fetch(`${backendUrl}/api/alumni/mutual-connections/${alumniId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch mutual connections');
      }

      const data = await response.json();
      setMutualConnections(data.mutual_connections || []);
      setHasLoaded(true); // Mark as loaded to prevent refetching
    } catch (err) {
      console.error('Error fetching mutual connections:', err);
      setError('Failed to load mutual connections');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDetails = () => {
    if (!showDetails && mutualConnections.length === 0 && !hasLoaded) {
      fetchMutualConnections();
    }
    setShowDetails(!showDetails);
  };

  // Don't render anything if there are no mutual connections
  if (mutualConnectionsCount === 0) {
    return null;
  }

  return (
    <div className="bg-gray-50 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Mutual Connections
          </h3>
          <p className="text-sm text-gray-600">
            {mutualConnectionsCount} connection{mutualConnectionsCount !== 1 ? 's' : ''} in common
          </p>
        </div>
        
        <button
          onClick={handleToggleDetails}
          disabled={loading}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          {showDetails ? 'Hide' : 'View'}
        </button>
      </div>

      {showDetails && (
        <div className="border-t border-gray-200 pt-4">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">Loading mutual connections...</span>
            </div>
          ) : error ? (
            <div className="text-center py-4">
              <p className="text-sm text-red-600">{error}</p>
              <button 
                onClick={fetchMutualConnections}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700"
              >
                Try again
              </button>
            </div>
          ) : mutualConnections.length > 0 ? (
            <div className="space-y-3">
              {mutualConnections.map((connection) => (
                <div key={connection.id} className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {connection.headshot_url ? (
                      <img
                        src={connection.headshot_url}
                        alt={`${connection.first_name} ${connection.last_name}`}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-medium text-xs">
                        {connection.first_name?.[0]}{connection.last_name?.[0]}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <button 
                      onClick={() => window.location.href = `/alumni/profile/${connection.id}`}
                      className="text-sm font-medium text-gray-900 hover:text-blue-600 cursor-pointer block text-left leading-tight"
                    >
                      {connection.first_name} {connection.last_name}
                    </button>
                    <div className="text-xs text-gray-500 space-y-1 mt-1">
                      {connection.current_position && connection.current_employer && (
                        <p className="leading-relaxed">
                          {connection.current_position}
                        </p>
                      )}
                      {connection.university && connection.graduation_year && (
                        <p className="leading-relaxed">
                          {connection.university} '{String(connection.graduation_year).slice(-2)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-600">No mutual connections found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MutualConnections;